import { useMsal } from "@azure/msal-react";
import React from "react";
import { getMicrosoftRedirectUri, loginRequest } from "./authConfig";
import { trackUiButtonEventAsync } from "../services/api/uiButtonStatsService";

type Props = {
    label?: string;
    loadingLabel?: string;
    className?: string;
    /** Classe du spinner (ex. `text-secondary` sur bouton blanc) */
    spinnerClass?: string;
    isProcessing?: boolean;
    redirectPath?: "/auth/login" | "/auth/signup";
    authIntent?: "login" | "signup";
    trackEvent?: string;
};

export function LoginButton({
    label = "Se connecter avec Microsoft",
    loadingLabel,
    className = "btn btn-outline-theme w-100",
    spinnerClass = "text-warning",
    isProcessing = false,
    redirectPath = "/auth/login",
    authIntent = "login",
    trackEvent,
}: Props): React.ReactElement {
    const { instance } = useMsal();
    const [isLoading, setIsLoading] = React.useState(false);
    const showLoading = isLoading || isProcessing;

    const handleLogin = async () => {
        setIsLoading(true);
        
        // Effacer le flag de déconnexion manuelle pour permettre la reconnexion
        sessionStorage.removeItem('microsoft_manual_logout');
        sessionStorage.setItem('microsoft_auth_intent', authIntent);
        
        try {
            if (trackEvent) {
                await trackUiButtonEventAsync(trackEvent);
            }
            // Utiliser loginRedirect au lieu de loginPopup pour éviter les problèmes de popup
            // La page entière sera redirigée vers Microsoft puis reviendra automatiquement
            await instance.loginRedirect({
                ...loginRequest,
                redirectUri: getMicrosoftRedirectUri(redirectPath),
            });
        } catch (e: unknown) {
            console.error('[LoginButton] Error during Microsoft login:', e);
            setIsLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className={className}
                onClick={() => void handleLogin()}
                disabled={showLoading}
                style={{ position: 'relative' }}
            >
                {showLoading ? (
                    <span className={`spinner-border spinner-border-sm ${spinnerClass} me-2`} role="status" aria-hidden="true"></span>
                ) : (
                    <svg viewBox="0 0 21 21" fill="currentColor">
                        <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                        <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                        <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                        <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                )}
                <span className="fw-bold">{showLoading ? (loadingLabel || label) : label}</span>
            </button>
        </>
    );
}
